/* eslint-disable react-hooks/exhaustive-deps */
// PortfolioContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { getUrl, uploadData } from "aws-amplify/storage";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { useParams, useNavigate } from "react-router-dom";

// Utility imports
import { calculateGroupData } from "../utils/groupDataCalculator";

export const PortfolioContext = createContext();

// Helper functions for time period matching
const isFinancialPeriod = (period) => {
  return period.match(/^T(12|6|3|1)\b/);
};

const getFinancialPeriods = (periods) => {
  return periods.filter(isFinancialPeriod);
};

const getPreferredPeriod = (periods) => {
  const financialPeriods = getFinancialPeriods(periods);
  const preferredOrder = ["T12", "T6", "T3", "T1"];
  for (const preferred of preferredOrder) {
    const found = financialPeriods.find((p) => p.startsWith(preferred + " "));
    if (found) return found;
  }
  return financialPeriods[0] || null;
};

// Helper function to convert to sentence case (first letter capitalized)
const toSentenceCase = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Extract a known sub-path (/financial-model, /finance-charts/..., /competition-charts/...)
// from the current URL so navigation between portfolios/facilities can preserve it.
const PORTFOLIO_SUB_PATH_PATTERN =
  /\/(financial-model|finance-charts\/[^/]+|competition-charts\/[^/]+)(?:\/.*)?$/;
const getCurrentPortfolioSubPath = () => {
  const pathname = window.location.pathname.replace(/\/+$/, "");
  const match = pathname.match(PORTFOLIO_SUB_PATH_PATTERN);
  return match ? match[0] : "";
};

// Helper function to sort periods in the correct order:
// 1. Years (2024, 2025, etc.) in ascending order
// 2. Trailing periods (T12, T6, T3, T1) in descending order
// 3. Projections (Projection 1, 2, 3) in ascending order
export const sortPortfolioPeriods = (periods) => {
  if (!periods || periods.length === 0) return periods;

  return [...periods].sort((a, b) => {
    // Check if period is a year (e.g., "2024")
    const isYearA = /^\d{4}$/.test(a);
    const isYearB = /^\d{4}$/.test(b);

    // Check if period is a trailing period (e.g., "T12 June 2025" or "T12")
    const trailingMatchA = a.match(/^T(\d+)/);
    const trailingMatchB = b.match(/^T(\d+)/);

    // Check if period is a projection (e.g., "Projection 1")
    const projectionMatchA = a.match(/Projection\s*(\d+)/i);
    const projectionMatchB = b.match(/Projection\s*(\d+)/i);

    // Both are years - sort in ascending order
    if (isYearA && isYearB) {
      return parseInt(a) - parseInt(b);
    }

    // Both are trailing periods - sort in descending order (T12 > T6 > T3 > T1)
    if (trailingMatchA && trailingMatchB) {
      const numA = parseInt(trailingMatchA[1]);
      const numB = parseInt(trailingMatchB[1]);
      return numB - numA; // Descending order
    }

    // Both are projections - sort in ascending order
    if (projectionMatchA && projectionMatchB) {
      const numA = parseInt(projectionMatchA[1]);
      const numB = parseInt(projectionMatchB[1]);
      return numA - numB; // Ascending order
    }

    // Mixed types - apply priority: Years first, then Trailing, then Projections
    if (isYearA) return -1;
    if (isYearB) return 1;

    if (trailingMatchA) return -1;
    if (trailingMatchB) return 1;

    if (projectionMatchA) return -1;
    if (projectionMatchB) return 1;

    // Default: maintain original order
    return 0;
  });
};

// Helper function to find the default period
// Prefers T3, then the closest trailing period to T3 (T1, T6, T12, etc.)
export const findDefaultPortfolioPeriod = (periods) => {
  if (!periods || periods.length === 0) return null;

  // First, try to find T3
  const t3Period = periods.find((p) => p.match(/^T3\b/));
  if (t3Period) return t3Period;

  // If no T3, find the closest trailing period to 3
  const trailingPeriods = periods
    .map((p) => {
      const match = p.match(/^T(\d+)/);
      return match ? { period: p, number: parseInt(match[1]) } : null;
    })
    .filter(Boolean);

  if (trailingPeriods.length > 0) {
    // Find the trailing period with number closest to 3
    const closest = trailingPeriods.reduce((prev, curr) => {
      const prevDiff = Math.abs(prev.number - 3);
      const currDiff = Math.abs(curr.number - 3);
      return currDiff < prevDiff ? curr : prev;
    });
    return closest.period;
  }

  // Fallback to first period if no trailing periods exist
  return periods[0];
};

export const PortfolioProvider = ({ children }) => {
  // State declarations
  const [portfolioData, setPortfolioData] = useState(null);
  const [consolidatedPortfolioData, setConsolidatedPortfolioData] =
    useState(null);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [currentPortfolioName, setCurrentPortfolioName] = useState(null);
  const [currentPortfolioFacility, setCurrentPortfolioFacility] =
    useState(null);
  const [currentPortfolioFacilityName, setCurrentPortfolioFacilityName] =
    useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [portfolioFacilityGroups, setPortfolioFacilityGroups] = useState([]);
  const [allPortfolioGroups, setAllPortfolioGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(false);
  const [dataInconsistencies, setDataInconsistencies] = useState([]);
  const [portfolioCompetitionData, setPortfolioCompetitionData] =
    useState(null);
  const [hasHandledInitialUrl, setHasHandledInitialUrl] = useState(false);
  const [portfolioFinanceData, setPortfolioFinanceData] = useState(null);

  const navigate = useNavigate();
  const { portfolioName: urlPortfolioName, facilityName: urlFacilityName } =
    useParams();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const user = await getCurrentUser();
        const session = await fetchAuthSession();

        if (session?.tokens?.accessToken?.toString()) {
          setIsAuthenticated(true);
          setAuthError(null);
        } else {
          throw new Error("No valid session found");
        }
      } catch (error) {
        setAuthError(error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Fetch portfolio groups when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPortfolioGroups();
    }
  }, [isAuthenticated]);

  // Function to fetch portfolio groups
  const fetchPortfolioGroups = async () => {
    if (!isAuthenticated) return;

    try {
      // Fetch portfolio groups data from S3
      try {
        const groupsUrl = await getUrl({
          path: "public/portfolioGroups.json",
        });

        const response = await fetch(groupsUrl.url.href);

        if (response.ok) {
          const groupsData = await response.json();

          // Normalize portfolioId to lowercase for consistent matching against consolidatedPortfolioData keys
          const normalizedGroupsData = groupsData.map((group) => ({
            ...group,
            portfolioId: group.portfolioId ? group.portfolioId.toLowerCase() : group.portfolioId,
          }));

          // Store ALL groups - this is important for preserving private groups
          setAllPortfolioGroups(normalizedGroupsData);

          // Filter groups based on privacy settings for display only
          const currentUser = await getCurrentUser();
          const filteredGroups = normalizedGroupsData.filter(
            (group) =>
              !group.isPrivate || group.createdBy === currentUser.username
          );
          setPortfolioFacilityGroups(filteredGroups);
        } else if (response.status !== 404) {
          // Error fetching groups
          console.warn("Error fetching portfolio groups");
        }
      } catch (error) {
        console.warn("Error in fetchPortfolioGroups:", error);
        setPortfolioFacilityGroups([]);
        setAllPortfolioGroups([]);
      }
    } catch (error) {
      console.error("Error in fetchPortfolioGroups:", error);
    }
  };

  // Function to create a new portfolio group
  const createPortfolioGroup = async (groupName, facilityIds, isPrivate) => {
    try {
      if (!currentPortfolioName || !groupName || facilityIds.length === 0) {
        return false;
      }

      const currentUser = await getCurrentUser();
      const newGroup = {
        id: `portfolio-group-${Date.now()}`,
        name: groupName,
        portfolioId: currentPortfolioName.toLowerCase(),
        facilityIds,
        isPrivate,
        createdBy: currentUser.username,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update the filtered groups for display
      const updatedFilteredGroups = [...portfolioFacilityGroups, newGroup];
      setPortfolioFacilityGroups(updatedFilteredGroups);

      // Update the complete list of ALL groups
      const updatedAllGroups = [...allPortfolioGroups, newGroup];
      setAllPortfolioGroups(updatedAllGroups);

      // Save the COMPLETE list of groups to S3
      await savePortfolioGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      console.error("Error creating portfolio group:", error);
      return false;
    }
  };

  // Function to delete a portfolio group
  const deletePortfolioGroup = async (groupId) => {
    try {
      // First check in the filtered list that's visible to the user
      const groupToDelete = portfolioFacilityGroups.find(
        (group) => group.id === groupId
      );
      if (!groupToDelete) {
        return false;
      }

      // Check if current user is the creator
      const currentUser = await getCurrentUser();
      if (groupToDelete.createdBy !== currentUser.username) {
        return false;
      }

      // Update both the filtered list and complete list
      const updatedFilteredGroups = portfolioFacilityGroups.filter(
        (group) => group.id !== groupId
      );
      setPortfolioFacilityGroups(updatedFilteredGroups);

      const updatedAllGroups = allPortfolioGroups.filter(
        (group) => group.id !== groupId
      );
      setAllPortfolioGroups(updatedAllGroups);

      // If current group is being deleted, clear it
      if (currentGroup?.id === groupId) {
        setCurrentGroup(null);
      }

      // Save the COMPLETE list of groups to S3
      await savePortfolioGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      console.error("Error deleting portfolio group:", error);
      return false;
    }
  };

  // Function to update a portfolio group
  const updatePortfolioGroup = async (groupId, updates) => {
    try {
      // Update in the filtered list
      const updatedFilteredGroups = portfolioFacilityGroups.map((group) =>
        group.id === groupId
          ? { ...group, ...updates, updatedAt: new Date().toISOString() }
          : group
      );
      setPortfolioFacilityGroups(updatedFilteredGroups);

      // Update in the complete list
      const updatedAllGroups = allPortfolioGroups.map((group) =>
        group.id === groupId
          ? { ...group, ...updates, updatedAt: new Date().toISOString() }
          : group
      );
      setAllPortfolioGroups(updatedAllGroups);

      // If current group is updated, update that reference too
      if (currentGroup?.id === groupId) {
        const updatedGroup = updatedAllGroups.find((g) => g.id === groupId);
        setCurrentGroup(updatedGroup);
      }

      // Save the COMPLETE list to S3
      await savePortfolioGroupsToS3(updatedAllGroups);

      return true;
    } catch (error) {
      console.error("Error updating portfolio group:", error);
      return false;
    }
  };

  // Function to select a portfolio group
  const selectPortfolioGroup = async (groupId, previousPeriod = null) => {
    setIsPortfolioLoading(true);

    try {
      // Ensure groupId has the prefix for comparison
      if (
        typeof groupId === "string" &&
        !groupId.startsWith("portfolio-group-")
      ) {
        groupId = `portfolio-group-${groupId}`;
      }

      const group = portfolioFacilityGroups.find((g) => g.id === groupId);

      if (!group) {
        setCurrentGroup(null);
        return;
      }

      // Get portfolio data directly
      const portfolioData = consolidatedPortfolioData[group.portfolioId];

      if (!portfolioData) {
        return;
      }

      // First update the portfolio context since group data depends on it
      setCurrentPortfolio(portfolioData);
      // Convert to sentence case for display
      setCurrentPortfolioName(toSentenceCase(group.portfolioId));

      const groupData = calculateGroupData(group, portfolioData);

      if (!groupData) {
        return;
      }

      // Force isGroup flag and groupId
      if (groupData.details) {
        groupData.details.isGroup = true;
        groupData.details.groupId = group.id;
        groupData.details.Facilities = `Group: ${group.name}`;
      }

      // Add a specific flag to ensure UI components recognize this as group data
      groupData._isGroupData = true;

      // Update state in sequence
      setCurrentGroup(group);
      setCurrentPortfolioFacility(groupData);

      // Set period
      const periods = Object.keys(groupData.periods || {});
      if (periods.length > 0) {
        let selectedPeriodValue;
        if (previousPeriod && periods.includes(previousPeriod)) {
          selectedPeriodValue = previousPeriod;
        } else {
          // Default to T3 or closest trailing period
          selectedPeriodValue = findDefaultPortfolioPeriod(periods);
        }
        setSelectedPeriod(selectedPeriodValue);
      }

      // Update URL to reflect both portfolio and group
      const currentPath = window.location.pathname;
      const expectedPath = `/portfolio/${encodeURIComponent(
        group.portfolioId
      )}`;
      if (currentPath !== expectedPath) {
        navigate(expectedPath);
      }
    } catch (error) {
      console.error("[ERROR] Error in selectPortfolioGroup:", error);
      // Reset states on error
      setCurrentGroup(null);
      setCurrentPortfolioFacility(null);
    } finally {
      setIsPortfolioLoading(false);
    }
  };

  // Helper function to save portfolio groups to S3
  const savePortfolioGroupsToS3 = async (groups) => {
    const blob = new Blob([JSON.stringify(groups)], {
      type: "application/json",
    });

    try {
      await uploadData({
        path: "public/portfolioGroups.json",
        data: blob,
      }).result;
    } catch (error) {
      console.error("Error saving portfolio groups to S3:", error);
      throw error;
    }
  };

  // Main fetch function to get portfolio data
  const fetchPortfolioData = async () => {
    try {
      setIsLoading(true);

      // Get portfolio facilities URL
      const portfolioFacilitiesUrl = await getUrl({
        path: `protected/current/PortfolioOverview.json`,
      });

      // Get portfolio competition data URL
      const portfolioCompetitionUrl = await getUrl({
        path: `protected/current/PortfolioCompetitionOverview.json`,
      });

      // Get portfolio finance data URL
      const portfolioFinanceUrl = await getUrl({
        path: `protected/PortfolioFinance.json`,
      });

      // Helper function to fetch and handle errors
      const fetchWithErrorHandling = async (url, name) => {
        try {
          const response = await fetch(url.url.href);

          if (!response.ok) {
            const errorStatus = response.status;
            console.error(
              `Error fetching ${name} (${errorStatus}):`,
              url.url.href
            );
            return {};
          }

          const data = await response.json();
          return data;
        } catch (error) {
          console.error(`Failed to fetch ${name}:`, error);
          return {};
        }
      };

      // Fetch portfolio data
      const portfolioFacilities = await fetchWithErrorHandling(
        portfolioFacilitiesUrl,
        "portfolioFacilities"
      );

      // Fetch portfolio competition data
      const portfolioCompetition = await fetchWithErrorHandling(
        portfolioCompetitionUrl,
        "portfolioCompetition"
      );

      // Fetch portfolio finance data
      const portfolioFinance = await fetchWithErrorHandling(
        portfolioFinanceUrl,
        "portfolioFinance"
      );

      if (
        !portfolioFacilities ||
        Object.keys(portfolioFacilities).length === 0
      ) {
        console.error("❌ Failed to load portfolio facilities data");
        alert(
          "Portfolio data failed to load. Please check your connection and try again."
        );
        setPortfolioData({});
        setIsLoading(false);
        return;
      }

      // Set portfolio competition data
      setPortfolioCompetitionData(portfolioCompetition);

      // Set portfolio finance data
      setPortfolioFinanceData(portfolioFinance);

      // Process portfolio data - transform it into a deal-like structure
      const processedPortfolioData = processPortfolioData(portfolioFacilities);

      // Check Castle portfolio specifically
      if (processedPortfolioData.Castle) {
        // Castle portfolio processing complete
      }

      // Enhance with financial data from S3
      let enhancedPortfolioData = processedPortfolioData;
      try {
        if (portfolioFinance && Object.keys(portfolioFinance).length > 0) {
          // Merge financial data from S3 into portfolio data
          enhancedPortfolioData = mergePortfolioFinanceData(
            processedPortfolioData,
            portfolioFinance
          );
        } else {
          console.warn("⚠️ No financial data available from S3");
        }
      } catch (error) {
        console.error("❌ Financial data integration failed:", error);
        enhancedPortfolioData = processedPortfolioData;
      }

      setPortfolioData(enhancedPortfolioData);
      setConsolidatedPortfolioData(enhancedPortfolioData);

      // Auto-select the first portfolio if no portfolio is currently selected
      const portfolioNames = Object.keys(enhancedPortfolioData);

      if (portfolioNames.length > 0 && !currentPortfolioName) {
        const firstPortfolio = portfolioNames[0];

        setCurrentPortfolio(enhancedPortfolioData[firstPortfolio]);
        // Convert to sentence case for display
        setCurrentPortfolioName(toSentenceCase(firstPortfolio));

        // Check if Consolidated facility has financial periods, otherwise select first individual facility
        const consolidatedFacility =
          enhancedPortfolioData[firstPortfolio].facilities["Consolidated"];
        const consolidatedPeriods = Object.keys(
          consolidatedFacility?.periods || {}
        );
        const hasFinancialPeriods = consolidatedPeriods.some(isFinancialPeriod);

        if (consolidatedFacility && hasFinancialPeriods) {
          // Use Consolidated if it has financial periods
          setCurrentPortfolioFacility(consolidatedFacility);
          setCurrentPortfolioFacilityName("Consolidated");

          // Set first available financial period
          const selectedPeriod = getPreferredPeriod(consolidatedPeriods);
          if (selectedPeriod) {
            setSelectedPeriod(selectedPeriod);
          }
        } else {
          // Fallback to first individual facility with financial periods

          const individualFacilities = Object.entries(
            enhancedPortfolioData[firstPortfolio].facilities
          ).filter(([name]) => name !== "Consolidated");

          for (const [facilityName, facility] of individualFacilities) {
            const periods = Object.keys(facility.periods || {});
            const hasFinancialPeriods = periods.some(isFinancialPeriod);

            if (hasFinancialPeriods) {
              setCurrentPortfolioFacility(facility);
              setCurrentPortfolioFacilityName(facilityName);

              // Set first available financial period
              const selectedPeriod = getPreferredPeriod(periods);
              if (selectedPeriod) {
                setSelectedPeriod(selectedPeriod);
              }
              break;
            }
          }

          // If no facility with financial periods found, fallback to Consolidated with Current periods
          if (!currentPortfolioFacilityName && consolidatedFacility) {
            setCurrentPortfolioFacility(consolidatedFacility);
            setCurrentPortfolioFacilityName("Consolidated");
            if (consolidatedPeriods.length > 0) {
              setSelectedPeriod(consolidatedPeriods[0]);
            }
          }
        }

        // Update URL to reflect the selected portfolio
        const encodedPortfolioName = encodeURIComponent(firstPortfolio);
        if (window.location.pathname === "/portfolio") {
          navigate(`/portfolio/${encodedPortfolioName}`);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error in fetchPortfolioData:", error);
      setPortfolioData({});
      setIsLoading(false);
    }
  };

  // Function to merge financial data from S3 into portfolio data
  const mergePortfolioFinanceData = (portfolioData, financeData) => {
    const mergedData = { ...portfolioData };

    // Iterate through each portfolio in the finance data
    for (const [portfolioName, portfolioFinance] of Object.entries(
      financeData
    )) {
      // Normalize portfolio name to lowercase to match processPortfolioData normalization
      const normalizedPortfolioName = portfolioName.toLowerCase();

      if (mergedData[normalizedPortfolioName]) {
        const facilities = portfolioFinance.facilities || {};
        const financeFacilityNames = Object.keys(facilities);
        const portfolioFacilityNames = Object.keys(
          mergedData[normalizedPortfolioName].facilities
        );

        // Merge financial data for each facility
        for (const [facilityName, facilityFinance] of Object.entries(
          facilities
        )) {
          const facilityPeriods = Object.keys(facilityFinance.periods || {});

          // Normalize periods — backend writes capital-P "Periods" for consolidated,
          // lowercase "periods" for individual facilities. Merge both so the frontend
          // always reads from lowercase "periods".
          const normalizedPeriods = {
            ...(facilityFinance.periods || {}),
            ...(facilityFinance.Periods || {}),
          };

          if (mergedData[normalizedPortfolioName].facilities[facilityName]) {
            // Merge the periods data (which contains all the financial metrics)
            mergedData[normalizedPortfolioName].facilities[
              facilityName
            ].periods = {
              ...mergedData[normalizedPortfolioName].facilities[facilityName]
                .periods,
              ...normalizedPeriods,
            };

            // Add any additional financial metrics if they exist
            if (facilityFinance.financialMetrics) {
              mergedData[normalizedPortfolioName].facilities[
                facilityName
              ].financialMetrics = facilityFinance.financialMetrics;
            }
          } else if (facilityName === "Consolidated") {
            // If Consolidated facility exists in finance data but not in portfolio data,
            // create it or update it with the financial periods

            mergedData[normalizedPortfolioName].facilities[facilityName] = {
              details: {
                Facilities: "Consolidated",
                Deal: normalizedPortfolioName,
              },
              periods: {
                ...normalizedPeriods,
              },
            };
          } else {
            console.warn(
              `   ⚠️ Facility "${facilityName}" not found in portfolio "${normalizedPortfolioName}". Available facilities: [${portfolioFacilityNames.join(
                ", "
              )}]`
            );
          }
        }
      } else {
        console.warn(
          `⚠️ Portfolio "${portfolioName}" (normalized: "${normalizedPortfolioName}") not found in mergedData. Available portfolios:`,
          Object.keys(mergedData)
        );
      }

      // Consolidated facility periods are computed on the backend and merged
      // in above alongside individual facilities — no frontend re-aggregation.
    }

    return mergedData;
  };

  // Function to create consolidated portfolio facility using group data logic
  const createConsolidatedPortfolioFacility = (portfolioName, facilities) => {
    if (!facilities || facilities.length === 0) {
      return {
        details: {
          Facilities: "Consolidated",
          Deal: portfolioName,
        },
        periods: {},
      };
    }

    // Helper function to calculate average of numeric values
    const calculateAverage = (values) => {
      const numericValues = values.filter(
        (v) => v !== null && v !== undefined && !isNaN(v)
      );
      if (numericValues.length === 0) return null;
      return (
        numericValues.reduce((sum, val) => sum + parseFloat(val), 0) /
        numericValues.length
      );
    };

    // Helper function to calculate sum of numeric values
    const calculateSum = (values) => {
      const numericValues = values.filter(
        (v) => v !== null && v !== undefined && !isNaN(v)
      );
      if (numericValues.length === 0) return null;
      return numericValues.reduce((sum, val) => sum + parseFloat(val), 0);
    };

    // Initialize consolidated details with averaged/summed values
    const consolidatedDetails = {
      Facilities: "Consolidated",
      Deal: portfolioName,
    };

    // Fields to average
    const fieldsToAverage = [
      "Occupancy Rate",
      "Star Rating",
      "Health Inspection Star Rating",
      "Staffing Star Rating",
      "Quality Measures Star Rating",
      "Nursing HPPD - Actual",
      "Nursing HPPD - Expected",
      "Nursing HPPD - Weekend",
      "Health Survey Score",
      "Medicare ADC",
    ];

    // Fields to sum
    const fieldsToSum = [
      "SNF Beds",
      "ALF Beds",
      "ILF Beds",
      "Total Beds",
      "Licensed Beds",
      "Average Daily Census",
    ];

    // Calculate averages
    fieldsToAverage.forEach((field) => {
      const values = facilities.map((f) => f.details[field]);
      const average = calculateAverage(values);
      if (average !== null) {
        consolidatedDetails[field] = Math.round(average * 100) / 100; // Round to 2 decimal places
      }
    });

    // Calculate sums
    fieldsToSum.forEach((field) => {
      const values = facilities.map((f) => f.details[field]);
      const sum = calculateSum(values);
      if (sum !== null) {
        consolidatedDetails[field] = Math.round(sum);
      }
    });

    // Take first non-null value for other fields
    const fieldsToTakeFirst = ["State", "Operator", "Owner", "Start Date"];
    fieldsToTakeFirst.forEach((field) => {
      const firstValue = facilities.find((f) => f.details[field])?.details[
        field
      ];
      if (firstValue) {
        consolidatedDetails[field] = firstValue;
      }
    });

    // Create consolidated details using facility data directly since periods come from S3 later
    // For now, just create the structure - periods will be added during S3 merge
    const consolidatedPeriods = {};

    const consolidatedFacility = {
      details: consolidatedDetails,
      periods: consolidatedPeriods,
    };

    return consolidatedFacility;
  };

  // Transform portfolio data into a structure similar to deal data
  const processPortfolioData = (portfolioData) => {
    const processed = {};

    // Check if portfolioData has the expected structure
    if (!portfolioData || typeof portfolioData !== "object") {
      console.warn("Portfolio data is not an object:", portfolioData);
      return processed;
    }

    // Handle the actual portfolio structure: {Deal: [array of portfolio groups]}
    if (portfolioData.Deal && Array.isArray(portfolioData.Deal)) {
      // Each element in the Deal array represents a portfolio group (by operator)
      portfolioData.Deal.forEach((portfolioGroup) => {
        if (
          !portfolioGroup ||
          !portfolioGroup.Deal ||
          !Array.isArray(portfolioGroup.Facilities)
        ) {
          console.warn("Invalid portfolio group structure:", portfolioGroup);
          return;
        }

        // Remove "Portfolio - " prefix (case insensitive) and normalize to lowercase
        const portfolioName = portfolioGroup.Deal.replace(
          /^portfolio\s*-\s*/i,
          ""
        ).toLowerCase(); // e.g., "crown" (normalized to match database portfolio_id)

        // Create sentence case version for display
        const portfolioNameDisplay = toSentenceCase(portfolioName);

        // Create the portfolio structure
        processed[portfolioName] = {
          facilities: {},
        };

        // Process each facility in this portfolio group
        portfolioGroup.Facilities.forEach((facilityData) => {
          const facilityName = facilityData.Facilities;

          if (!facilityName) {
            console.warn("Facility missing name:", facilityData);
            return;
          }

          // Add individual facility to this portfolio
          processed[portfolioName].facilities[facilityName] = {
            details: {
              Facilities: facilityName,
              Deal: portfolioNameDisplay,
              CCN: facilityData.CCN,
              Address: facilityData.Address,
              "Street Address": facilityData["Street Address"],
              "City/Town": facilityData["City/Town"],
              State: facilityData.State,
              Zip: facilityData.Zip,
              "City, State": facilityData["City, State"],
              "SNF Beds": facilityData["SNF Beds"],
              "ALF Beds": facilityData["ALF Beds"],
              "ILF Beds": facilityData["ILF Beds"],
              "Total Beds": facilityData["Total Beds"],
              "Star Rating": facilityData["Star Rating"],
              "Health Inspection Star Rating":
                facilityData["Health Inspection Star Rating"],
              "Staffing Star Rating": facilityData["Staffing Star Rating"],
              "Quality Measures Star Rating":
                facilityData["Quality Measures Star Rating"],
              "Nursing HPPD - Actual": facilityData["Nursing HPPD - Actual"],
              "Nursing HPPD - Expected":
                facilityData["Nursing HPPD - Expected"],
              "Nursing HPPD - Weekend": facilityData["Nursing HPPD - Weekend"],
              Coordinates: facilityData.coordinates,
              "Deal Name": facilityData["Deal Name"],
              Operator: facilityData.Operator,
              "Start Date": facilityData["Start Date"],
              EIN: facilityData.EIN,
              "Medicare Number": facilityData["Medicare Number"],
              "Medicaid Number": facilityData["Medicaid Number"],
              "Licensed Beds": facilityData["Licensed Beds"],
              "Average Daily Census": facilityData["Average Daily Census"],
              "Health Survey Score": facilityData["Health Survey Score"],
              Owner: facilityData.Owner,
              "Provider Name": facilityData["Provider Name"],
              "Occupancy Rate": facilityData["Occupancy Rate"],
              "Medicare ADC": facilityData["Medicare ADC"],
            },
            periods: {},
          };
        });

        // Detect AL/IL duplicates: facilities whose name ends with " AL"
        // (or whose CCN ends with "AL") are co-located with an SNF and
        // tracked separately.  Flag them so they can be excluded from the
        // Consolidated aggregation (the SNF entry already covers the building).
        const allEntries = Object.values(
          processed[portfolioName].facilities
        );
        allEntries.forEach((f) => {
          const name = String(f.details?.Facilities || "").trim();
          const ccn = String(f.details?.CCN || "").trim();
          if (
            /\b(AL|IL)$/i.test(name) ||
            /(AL|IL)$/i.test(ccn)
          ) {
            f.details.isALDuplicate = true;
          }
        });

        // Create consolidated facility excluding AL duplicates to prevent
        // double-counting (the SNF entry already covers the building)
        const facilitiesForConsolidation = allEntries.filter(
          (f) => !f.details?.isALDuplicate
        );
        if (facilitiesForConsolidation.length > 0) {
          const consolidatedFacility = createConsolidatedPortfolioFacility(
            portfolioNameDisplay,
            facilitiesForConsolidation
          );
          processed[portfolioName].facilities["Consolidated"] =
            consolidatedFacility;
        }

        // Compute unique facility count by deduplicating co-located buildings
        let uniqueCount = 0;
        const seenAddresses = new Set();
        allEntries.forEach((f) => {
          const addr = (
            f.details?.Address ||
            f.details?.["Street Address"] ||
            ""
          )
            .trim()
            .toLowerCase();
          if (!addr || !seenAddresses.has(addr)) {
            uniqueCount++;
            if (addr) seenAddresses.add(addr);
          }
        });
        processed[portfolioName].uniqueFacilityCount = uniqueCount;
      });
    }

    return processed;
  };

  // Function to select a portfolio
  const selectPortfolio = async (portfolioName, previousPeriod = null) => {
    setIsPortfolioLoading(true);

    try {
      if (!portfolioName) {
        setCurrentPortfolio(null);
        setCurrentPortfolioName(null);
        setCurrentPortfolioFacility(null);
        setCurrentPortfolioFacilityName(null);
        setSelectedPeriod(null);
        setCurrentGroup(null);

        if (window.location.pathname !== "/portfolio") {
          navigate("/portfolio");
        }
        return;
      }

      if (
        consolidatedPortfolioData &&
        consolidatedPortfolioData[portfolioName]
      ) {
        const portfolio = consolidatedPortfolioData[portfolioName];

        setCurrentPortfolio(portfolio);
        // Convert to sentence case for display
        setCurrentPortfolioName(toSentenceCase(portfolioName));

        // Financial data is now loaded from S3, no special processing needed

        // Auto-select consolidated facility
        const consolidatedFacility = portfolio.facilities["Consolidated"];
        if (consolidatedFacility) {
          // Set the facility immediately with existing data
          setCurrentPortfolioFacility(consolidatedFacility);
          setCurrentPortfolioFacilityName("Consolidated");

          // Handle period selection with existing data
          const periods = Object.keys(consolidatedFacility.periods || {});
          if (periods.length > 0) {
            // Preserve current period if it exists, otherwise use previousPeriod, then default to T3 or closest
            const currentPeriod = selectedPeriod;
            const periodToSelect =
              currentPeriod && periods.includes(currentPeriod)
                ? currentPeriod
                : previousPeriod && periods.includes(previousPeriod)
                ? previousPeriod
                : findDefaultPortfolioPeriod(periods);
            setSelectedPeriod(periodToSelect);
          }
        }

        // Update URL — preserve any active sub-path (financial-model, chart pages)
        // so switching portfolios doesn't kick the user back to the overview.
        const encodedPortfolioName = encodeURIComponent(portfolioName);
        const subPath = getCurrentPortfolioSubPath();
        const targetPath = `/portfolio/${encodedPortfolioName}${subPath}`;
        if (window.location.pathname !== targetPath) {
          navigate(targetPath);
        }
      }
    } catch (error) {
      console.error("Error selecting portfolio:", error);
    } finally {
      setIsPortfolioLoading(false);
    }
  };

  // Function to select a portfolio facility
  const selectPortfolioFacility = async (
    facilityName,
    previousPeriod = null
  ) => {
    setIsPortfolioLoading(true);

    try {
      if (!facilityName) {
        // If no facility specified, go back to consolidated
        if (currentPortfolioName) {
          // Convert to lowercase for selectPortfolio since it expects lowercase for data access
          selectPortfolio(currentPortfolioName.toLowerCase(), previousPeriod);
        }
        return;
      }

      if (consolidatedPortfolioData && currentPortfolioName) {
        // Convert to lowercase for data access since keys are lowercase
        const portfolioKey = currentPortfolioName.toLowerCase();

        if (consolidatedPortfolioData[portfolioKey]) {
          const facility =
            consolidatedPortfolioData[portfolioKey].facilities[facilityName];

          if (facility) {
            // Set the facility immediately with existing data
            setCurrentPortfolioFacility(facility);
            setCurrentPortfolioFacilityName(facilityName);

            // Handle period selection with existing data
            const periods = Object.keys(facility.periods || {});

            if (periods.length > 0) {
              // Preserve current period if it exists in new facility, otherwise use previousPeriod, then default to T3 or closest
              const currentPeriod = selectedPeriod;
              const periodToSelect =
                currentPeriod && periods.includes(currentPeriod)
                  ? currentPeriod
                  : previousPeriod && periods.includes(previousPeriod)
                  ? previousPeriod
                  : findDefaultPortfolioPeriod(periods);
              setSelectedPeriod(periodToSelect);
            } else {
              console.warn(
                `   ⚠️ No periods available for facility "${facilityName}"`
              );
              setSelectedPeriod(null);
            }

            // Time periods are now pre-calculated and loaded from S3

            // Update URL - use lowercase for consistency with data keys.
            // Preserve any active sub-path (financial-model, chart pages) so
            // switching facilities from those views doesn't kick the user back
            // to the overview.
            const encodedPortfolioName = encodeURIComponent(portfolioKey);
            const encodedFacilityName = encodeURIComponent(facilityName);
            const subPath = getCurrentPortfolioSubPath();
            const targetPath = `/portfolio/${encodedPortfolioName}/${encodedFacilityName}${subPath}`;
            if (window.location.pathname !== targetPath) {
              navigate(targetPath);
            }
          } else {
            console.error(
              `   ❌ Facility "${facilityName}" not found in portfolio "${portfolioKey}"`
            );
          }
        } else {
          console.error(
            `   ❌ Portfolio "${portfolioKey}" not found in consolidatedPortfolioData`
          );
        }
      } else {
        console.error(
          `   ❌ Missing consolidatedPortfolioData or currentPortfolioName. consolidatedPortfolioData: ${!!consolidatedPortfolioData}, currentPortfolioName: "${currentPortfolioName}"`
        );
      }
    } catch (error) {
      console.error("Error selecting portfolio facility:", error);
    } finally {
      setIsPortfolioLoading(false);
    }
  };

  // Initialize portfolio data on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchPortfolioData();
    }
  }, [isAuthenticated]);

  // Effect to handle initial URL-based portfolio selection
  useEffect(() => {
    if (!consolidatedPortfolioData || !isAuthenticated || hasHandledInitialUrl)
      return;

    const path = window.location.pathname;
    const portfolioMatch = path.match(/\/portfolio\/([^/]+)/);

    if (portfolioMatch && portfolioMatch[1]) {
      const portfolioNameFromUrl = decodeURIComponent(
        portfolioMatch[1]
      ).toLowerCase();

      if (consolidatedPortfolioData[portfolioNameFromUrl]) {
        setHasHandledInitialUrl(true);

        // Check if there's also a facility in the URL
        const facilityMatch = path.match(/\/portfolio\/[^/]+\/([^/]+)/);
        if (facilityMatch && facilityMatch[1]) {
          const facilityNameFromUrl = decodeURIComponent(facilityMatch[1]);
          // First select portfolio, then facility
          selectPortfolio(portfolioNameFromUrl).then(() => {
            selectPortfolioFacility(facilityNameFromUrl);
          });
        } else {
          // Just select portfolio
          selectPortfolio(portfolioNameFromUrl);
        }
      } else {
        setHasHandledInitialUrl(true);
        navigate("/portfolio");
      }
    } else {
      setHasHandledInitialUrl(true);
    }
  }, [
    consolidatedPortfolioData,
    isAuthenticated,
    hasHandledInitialUrl,
    selectPortfolio,
    selectPortfolioFacility,
    navigate,
  ]);

  // Effect to handle URL changes for portfolio navigation (only for browser back/forward)
  useEffect(() => {
    if (!consolidatedPortfolioData || !isAuthenticated) return;

    const handleUrlChange = () => {
      const path = window.location.pathname;
      const portfolioMatch = path.match(/\/portfolio\/([^/]+)/);

      if (portfolioMatch && portfolioMatch[1]) {
        const portfolioNameFromUrl = decodeURIComponent(
          portfolioMatch[1]
        ).toLowerCase();

        // Compare lowercase versions since currentPortfolioName is now uppercase for display
        const portfolioChanged =
          portfolioNameFromUrl !== currentPortfolioName?.toLowerCase();

        // Check if there's also a facility in the URL
        const facilityMatch = path.match(/\/portfolio\/[^/]+\/([^/]+)/);
        const facilityNameFromUrl =
          facilityMatch && facilityMatch[1]
            ? decodeURIComponent(facilityMatch[1])
            : null;

        const facilityChanged =
          facilityNameFromUrl &&
          facilityNameFromUrl !== currentPortfolioFacilityName;

        if (portfolioChanged || facilityChanged) {
          if (consolidatedPortfolioData[portfolioNameFromUrl]) {
            if (facilityNameFromUrl) {
              // If URL has a facility, select both portfolio and facility
              if (portfolioChanged) {
                selectPortfolio(portfolioNameFromUrl).then(() => {
                  selectPortfolioFacility(facilityNameFromUrl, selectedPeriod);
                });
              } else if (facilityChanged) {
                // Portfolio is same, just change facility
                selectPortfolioFacility(facilityNameFromUrl, selectedPeriod);
              }
            } else {
              // No facility in URL, just select portfolio (which will default to Consolidated)
              if (portfolioChanged) {
                selectPortfolio(portfolioNameFromUrl, selectedPeriod);
              }
            }
          } else {
            navigate("/portfolio");
          }
        }
      } else if (path === "/portfolio" && currentPortfolioName) {
        // Clear selection when going back to portfolio list
        setCurrentPortfolio(null);
        setCurrentPortfolioName(null);
        setCurrentPortfolioFacility(null);
        setCurrentPortfolioFacilityName(null);
        setSelectedPeriod(null);
        setCurrentGroup(null);
      }
    };

    // Listen for history changes (back/forward buttons)
    // NOTE: This only fires on browser navigation, not programmatic navigate() calls
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [
    consolidatedPortfolioData,
    currentPortfolioName,
    currentPortfolioFacilityName,
    selectedPeriod,
    isAuthenticated,
    selectPortfolio,
    selectPortfolioFacility,
    navigate,
  ]);

  // Get all portfolio names
  const portfolioNames = consolidatedPortfolioData
    ? Object.keys(consolidatedPortfolioData)
    : [];

  // Unique facility count for the current portfolio (deduplicates co-located SNF/AL)
  const uniqueFacilityCount =
    consolidatedPortfolioData &&
    currentPortfolioName &&
    consolidatedPortfolioData[currentPortfolioName.toLowerCase()]
      ? consolidatedPortfolioData[currentPortfolioName.toLowerCase()]
          .uniqueFacilityCount
      : 0;

  // Get portfolio facility names for the current portfolio
  const portfolioFacilityNames =
    consolidatedPortfolioData &&
    currentPortfolioName &&
    consolidatedPortfolioData[currentPortfolioName.toLowerCase()]
      ? Object.keys(
          consolidatedPortfolioData[currentPortfolioName.toLowerCase()]
            .facilities
        ).filter((name) => name.toLowerCase() !== "consolidated")
      : [];

  // Get periods from current portfolio facility (includes GraphQL time periods)
  // Sort periods in the correct order: T12, T6, T3, T1
  const periodsList = currentPortfolioFacility
    ? sortPortfolioPeriods(Object.keys(currentPortfolioFacility.periods || {}))
    : [];

  // Financial data utility functions - now using S3 data
  const refreshFinancialData = async (portfolioName, datekey = null) => {
    if (!portfolioName) return;

    try {
      setIsPortfolioLoading(true);
      // Financial data is now refreshed by re-fetching from S3
      await fetchPortfolioData();
    } catch (error) {
      console.error("Error refreshing financial data:", error);
    } finally {
      setIsPortfolioLoading(false);
    }
  };

  const getFacilityFinancialData = async (locationId, datekey) => {
    // Financial data is now available directly from portfolioFinanceData
    if (!portfolioFinanceData) return null;

    // Find the facility with matching location_id
    for (const portfolio of Object.values(portfolioFinanceData)) {
      for (const facility of Object.values(portfolio.facilities || {})) {
        if (
          facility.details?.CCN === locationId ||
          facility.details?.ccn === locationId
        ) {
          return facility.periods || {};
        }
      }
    }
    return null;
  };

  const getFacilityHistoricalData = async (locationId, startDate, endDate) => {
    // Historical data is now part of the pre-calculated periods from S3
    const facilityData = await getFacilityFinancialData(locationId, null);
    return facilityData || {};
  };

  // Context value
  const value = {
    // Data
    portfolioData,
    consolidatedPortfolioData,
    portfolioNames,
    currentPortfolio,
    currentPortfolioName,
    currentPortfolioFacility,
    currentPortfolioFacilityName,
    portfolioFacilityNames,
    uniqueFacilityCount,
    portfolioCompetitionData,
    portfolioFinanceData,

    // State
    selectedPeriod,
    setSelectedPeriod,
    isLoading,
    isPortfolioLoading,
    isAuthenticated,
    authError,
    dataInconsistencies,

    // Groups
    portfolioFacilityGroups,
    allPortfolioGroups,
    currentGroup,
    setCurrentGroup,

    // Actions
    selectPortfolio,
    selectPortfolioFacility,
    selectPortfolioGroup,
    fetchPortfolioData,
    createPortfolioGroup,
    deletePortfolioGroup,
    updatePortfolioGroup,
    fetchPortfolioGroups,

    // Financial Data
    refreshFinancialData,
    getFacilityFinancialData,
    getFacilityHistoricalData,

    // Periods
    periodsList,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolioData = () => useContext(PortfolioContext);
